import { useState, useRef } from "react"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Camera, Upload, Loader2, X } from "lucide-react"
import { toast } from "sonner"

interface ImageScannerProps {
  categories: string[]
  onDataParsed: (data: any) => void
}

export function ImageScanner({ categories, onDataParsed }: ImageScannerProps) {
  const [image, setImage] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Security validation with magic number verification
    const { validateImageFile, formatFileSize } = await import("@/lib/utils/file-validation")
    const validation = await validateImageFile(file)

    if (!validation.isValid) {
      toast.error(validation.error || "Invalid file")
      // Reset input
      if (fileInputRef.current) fileInputRef.current.value = ""
      if (cameraInputRef.current) cameraInputRef.current.value = ""
      return
    }

    const reader = new FileReader()
    reader.onload = (event) => {
      const result = event.target?.result as string
      setImage(result)
      toast.success(`${validation.detectedType} file validated successfully`)
    }
    reader.onerror = () => {
      toast.error("Failed to read file. Please try again")
    }
    reader.readAsDataURL(file)
  }

  const handleAnalyze = async () => {
    if (!image) {
      toast.error("Please select an image first")
      return
    }

    setIsProcessing(true)

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY

      const response = await fetch(`${supabaseUrl}/functions/v1/parse-image`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${supabaseAnonKey}`
        },
        body: JSON.stringify({
          image,
          categories
        })
      })

      if (!response.ok) {
        throw new Error("Failed to parse image")
      }

      const data = await response.json()

      // AI returns { documentType, data }
      const successMessage = data.documentType === "receipt"
        ? "Receipt parsed successfully!"
        : `Found ${data.data.totalCount} transaction${data.data.totalCount !== 1 ? "s" : ""}`

      toast.success(successMessage)

      onDataParsed(data)

      // Clear image after successful parse
      setImage(null)
    } catch (error) {
      console.error("Image parsing error:", error)
      toast.error("Failed to parse image. Please try again.")
    } finally {
      setIsProcessing(false)
    }
  }

  const clearImage = () => {
    setImage(null)
    if (fileInputRef.current) fileInputRef.current.value = ""
    if (cameraInputRef.current) cameraInputRef.current.value = ""
  }

  return (
    <div className="space-y-4">
      {!image ? (
        <div className="space-y-3">
          {/* Upload Button */}
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            className="w-full h-32 flex flex-col items-center justify-center gap-3 border-2 border-dashed"
            disabled={isProcessing}
          >
            <Upload className="h-8 w-8 text-muted-foreground" />
            <div className="text-center">
              <p className="font-medium">Upload Document</p>
              <p className="text-xs text-muted-foreground mt-1">
                Receipt or bank statement
              </p>
            </div>
          </Button>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
          />

          {/* Camera Button (Mobile) */}
          <Button
            type="button"
            variant="outline"
            onClick={() => cameraInputRef.current?.click()}
            className="w-full h-24 flex flex-col items-center justify-center gap-2"
            disabled={isProcessing}
          >
            <Camera className="h-6 w-6" />
            <span className="text-sm font-medium">Take Photo</span>
          </Button>

          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileUpload}
            className="hidden"
          />
        </div>
      ) : (
        <div className="space-y-3">
          {/* Image Preview */}
          <Card className="relative overflow-hidden">
            <div className="relative aspect-video w-full bg-muted">
              <img
                src={image}
                alt="Scanned image preview"
                className="absolute inset-0 w-full h-full object-contain"
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={clearImage}
              className="absolute top-2 right-2 bg-background/80 backdrop-blur-sm"
              disabled={isProcessing}
            >
              <X className="h-4 w-4" />
            </Button>
          </Card>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={clearImage}
              disabled={isProcessing}
            >
              Choose Different
            </Button>
            <Button
              type="button"
              onClick={handleAnalyze}
              disabled={isProcessing}
              className="bg-accent hover:bg-accent/90 text-accent-foreground"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Analyzing...
                </>
              ) : (
                "Analyze"
              )}
            </Button>
          </div>

          {/* Info */}
          <p className="text-xs text-center text-muted-foreground">
            AI will automatically detect and extract transaction details
          </p>
        </div>
      )}
    </div>
  )
}
